/**
 * Media Picker — Upload images or search Unsplash.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Upload, Search, Image, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

interface MediaPickerProps {
  projectId: string;
  jobId: string;
  onSelect: (url: string) => void;
  onClose: () => void;
}

type Tab = 'library' | 'upload' | 'unsplash';

export function MediaPicker({ projectId, jobId, onSelect, onClose }: MediaPickerProps) {
  const [tab, setTab] = useState<Tab>('library');
  const [searchQuery, setSearchQuery] = useState('');
  const queryClient = useQueryClient();

  // Fetch project media
  const { data: mediaList, isLoading: mediaLoading } = useQuery({
    queryKey: ['editor-media', projectId, jobId],
    queryFn: async () => {
      const res = await api.get(`/editor/${projectId}/${jobId}/media`);
      return res.data.data || [];
    },
  });

  // Search Unsplash
  const {
    data: unsplashResults,
    isLoading: unsplashLoading,
    refetch: searchUnsplash,
  } = useQuery({
    queryKey: ['unsplash-search', searchQuery],
    queryFn: async () => {
      if (!searchQuery.trim()) return [];
      const res = await api.get(`/editor/unsplash/search?q=${encodeURIComponent(searchQuery)}`);
      return res.data.data || [];
    },
    enabled: false,
  });

  // Upload file
  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/editor/${projectId}/${jobId}/media`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['editor-media', projectId, jobId] });
      toast.success('Image uploaded');
      onSelect(data.url);
    },
    onError: () => toast.error('Upload failed'),
  });

  // Delete media
  const deleteMutation = useMutation({
    mutationFn: async (filename: string) => {
      await api.delete(`/editor/${projectId}/${jobId}/media/${filename}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['editor-media', projectId, jobId] });
      toast.success('Deleted');
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadMutation.mutate(file);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) searchUnsplash();
  };

  const tabs = [
    { id: 'library' as Tab, label: 'Library', icon: Image },
    { id: 'upload' as Tab, label: 'Upload', icon: Upload },
    { id: 'unsplash' as Tab, label: 'Unsplash', icon: Search },
  ];

  return (
    <div className="flex w-80 flex-col border-l bg-white">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-4 py-3">
        <span className="text-sm font-semibold">Media</span>
        <button onClick={onClose} className="rounded p-1 hover:bg-gray-100">
          <X className="h-4 w-4 text-gray-400" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex flex-1 items-center justify-center gap-1.5 border-b-2 py-2.5 text-xs font-medium transition-colors ${
              tab === id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-3">
        {/* Library tab */}
        {tab === 'library' && (
          <div className="space-y-2">
            {mediaLoading && (
              <div className="flex justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              </div>
            )}
            {!mediaLoading && (!mediaList || mediaList.length === 0) && (
              <p className="py-8 text-center text-xs text-gray-400">No media uploaded yet</p>
            )}
            <div className="grid grid-cols-2 gap-2">
              {mediaList?.map((item: any) => (
                <div
                  key={item.key}
                  className="group relative aspect-square cursor-pointer overflow-hidden rounded-lg border bg-gray-100 hover:ring-2 hover:ring-blue-300"
                  onClick={() => onSelect(item.key)}
                >
                  <div className="flex h-full items-center justify-center text-xs text-gray-400">
                    <Image className="h-6 w-6" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 truncate bg-black/60 px-1.5 py-1 text-[10px] text-white">
                    {item.filename}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteMutation.mutate(item.filename);
                    }}
                    className="absolute right-1 top-1 hidden h-5 w-5 items-center justify-center rounded bg-red-500 text-white group-hover:flex"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upload tab */}
        {tab === 'upload' && (
          <div className="space-y-4">
            <label className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-6 py-12 transition-colors hover:border-blue-400 hover:bg-blue-50">
              {uploadMutation.isPending ? (
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              ) : (
                <>
                  <Upload className="h-8 w-8 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-600">Click to upload</p>
                  <p className="text-xs text-gray-400">PNG, JPG, GIF, WebP, SVG (max 10 MB)</p>
                </>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
                disabled={uploadMutation.isPending}
              />
            </label>
          </div>
        )}

        {/* Unsplash tab */}
        {tab === 'unsplash' && (
          <div className="space-y-3">
            <form onSubmit={handleSearch} className="flex gap-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search photos..."
                className="flex-1 rounded-md border px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
              <button
                type="submit"
                className="rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
              >
                <Search className="h-4 w-4" />
              </button>
            </form>

            {unsplashLoading && (
              <div className="flex justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              {unsplashResults?.map((img: any) => (
                <div
                  key={img.id}
                  className="group relative aspect-video cursor-pointer overflow-hidden rounded-lg bg-gray-100 hover:ring-2 hover:ring-blue-300"
                  onClick={() => onSelect(img.url)}
                >
                  <img
                    src={img.thumbUrl}
                    alt={img.alt}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute bottom-0 left-0 right-0 truncate bg-black/60 px-1.5 py-0.5 text-[10px] text-white">
                    by {img.photographer}
                  </div>
                </div>
              ))}
            </div>

            {unsplashResults && unsplashResults.length === 0 && searchQuery && (
              <p className="py-4 text-center text-xs text-gray-400">No results found</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
